import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Sliders, Eye, EyeOff, Save, Download, Lock, Unlock, Pin, Pencil } from "lucide-react";
import { EditRankDialog } from "@/components/admin/EditRankDialog";

const POSITION_FILTERS = [
  { v: "", l: "All Positions" }, { v: "P", l: "All Pitchers" }, { v: "IF", l: "All Infielders" }, { v: "OF", l: "All Outfielders" },
  ...["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "UTIL", "DH"].map((p) => ({ v: p, l: p })),
];
const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1));

export default function AdminRankings() {
  const [weights, setWeights] = useState({ stat_weight: 0.5, metric_weight: 0.3, evaluation_weight: 0.2 });
  const [config, setConfig] = useState({ public_visible: true });
  const [gated, setGated] = useState(false);
  const [players, setPlayers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingWeights, setSavingWeights] = useState(false);
  const [posFilter, setPosFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const loadPlayers = (pos = posFilter) =>
    api.get("/admin/rankings/players", { params: pos ? { primary_position: pos } : {} })
      .then(({ data }) => setPlayers(data.items || []));

  useEffect(() => {
    api.get("/admin/rating-weights").then(({ data }) => setWeights(data)).catch(() => {});
    api.get("/admin/rankings/config").then(({ data }) => setConfig({ public_visible: data.public_visible !== false })).catch(() => {});
    api.get("/admin/subscription/config").then(({ data }) => setGated(!!data.leaderboards_gated)).catch(() => {});
    loadPlayers("");
  }, []);

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      const { data } = await api.patch("/admin/rating-weights", { ...weights, recompute: true });
      toast.success(`Weights saved · ${data.players_recomputed} players recomputed`);
      loadPlayers();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingWeights(false);
    }
  };

  const toggleVisibility = async () => {
    const next = !config.public_visible;
    try {
      await api.patch("/admin/rankings/config", { public_visible: next });
      setConfig({ public_visible: next });
      toast.success(next ? "Rankings are now PUBLIC" : "Rankings are now HIDDEN from public");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const toggleGate = async () => {
    const next = !gated;
    try {
      await api.patch("/admin/subscription/config", { leaderboards_gated: next });
      setGated(next);
      toast.success(next ? "Full rankings & leaderboards now require a membership" : "Full rankings & leaderboards are now open to everyone");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const recomputeAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/rankings/recompute");
      toast.success(`Recomputed ${data.players_recomputed} player ratings`);
      loadPlayers();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleHidden = async (p) => {
    const next = !p.hidden_from_rankings;
    try {
      await api.patch(`/admin/players/${p.id}/ranking`, { hidden_from_rankings: next });
      loadPlayers();
      toast.success(next ? `${p.first_name} hidden from rankings` : `${p.first_name} shown in rankings`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const setOverride = async (p, rank_override) => {
    try {
      await api.patch(`/admin/players/${p.id}/ranking`, { rank_override });
      setEditing(null);
      loadPlayers();
      toast.success(rank_override == null ? "Override cleared" : `Pinned ${p.first_name} to #${rank_override}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="label-overline text-[#E63946]">Rankings Engine</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 data-testid="admin-rankings-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Ranking Controls</h1>
            <p className="text-xs text-slate-500 mt-1">Tune the CBR Overall Rating formula, recompute ratings, and control public ranking visibility.</p>
          </div>
          <button
            data-testid="export-rankings-csv"
            onClick={() => downloadFile("/admin/rankings/export.csv", {}, "cbr-rankings.csv")
              .then(() => toast.success("Rankings CSV exported"))
              .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Export failed"))}
            className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> Export Rankings CSV
          </button>
        </div>

        {/* Controls row */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {/* Rating weights */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center gap-2 label-overline text-[#0A192F]"><Sliders className="w-4 h-4" /> Rating Weights</div>
            <p className="text-xs text-slate-500 mt-1">Blend of game stats, verified metrics and evaluator OFV. Weights are normalized; a missing component counts as an average 50.</p>
            <div className="mt-4 space-y-4">
              <WeightSlider label="Game Stats (In-Game Performance)" value={weights.stat_weight} onChange={(v) => setWeights((w) => ({ ...w, stat_weight: v }))} testid="weight-stat" />
              <WeightSlider label="Verified Metrics" value={weights.metric_weight} onChange={(v) => setWeights((w) => ({ ...w, metric_weight: v }))} testid="weight-metric" />
              <WeightSlider label="Evaluations (OFV)" value={weights.evaluation_weight} onChange={(v) => setWeights((w) => ({ ...w, evaluation_weight: v }))} testid="weight-eval" />
            </div>
            <button data-testid="save-weights-button" onClick={saveWeights} disabled={savingWeights} className="mt-5 inline-flex items-center gap-2 bg-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
              <Save className="w-4 h-4" /> {savingWeights ? "Saving…" : "Save & Recompute"}
            </button>
          </div>

          {/* Visibility + recompute */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-col">
            <div className="flex items-center gap-2 label-overline text-[#0A192F]">Public Visibility</div>
            <p className="text-xs text-slate-500 mt-1">When hidden, the public Rankings page shows a "coming soon" state.</p>
            <button
              data-testid="toggle-rankings-visibility"
              onClick={toggleVisibility}
              className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm ${
                config.public_visible ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"
              }`}
            >
              {config.public_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {config.public_visible ? "Rankings Public" : "Rankings Hidden"}
            </button>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-2 label-overline text-[#0A192F]">Membership Wall</div>
              <p className="text-xs text-slate-500 mt-1">When ON, the full Rankings, Team Rankings & Leaderboards pages require an active membership. The homepage Top-5 preview stays public.</p>
              <button
                data-testid="toggle-membership-gate"
                onClick={toggleGate}
                className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm ${
                  gated ? "bg-[#E63946] text-white" : "bg-slate-300 text-slate-700"
                }`}
              >
                {gated ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                {gated ? "Membership Required" : "Open to Everyone"}
              </button>
            </div>

            <div className="mt-auto pt-6">
              <div className="label-overline text-[#0A192F]">Recompute All</div>
              <p className="text-xs text-slate-500 mt-1">Recalculate every player&apos;s CBR Overall Rating.</p>
              <button data-testid="recompute-all-button" onClick={recomputeAll} disabled={busy} className="mt-3 inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Recomputing…" : "Recompute Rankings"}
              </button>
            </div>
          </div>
        </div>

        {/* Player ranking table */}
        <div className="flex items-end justify-between gap-4 flex-wrap mt-10">
          <div>
            <h2 className="font-display text-2xl uppercase tracking-tight">Live Ranking Order</h2>
            <p className="text-xs text-slate-500 mt-1">Exactly what the public sees. Use <b>Edit Rank</b> to pin a player to a national slot. Stats are regressed toward 50 until a player has enough plate appearances / innings; missing parts count as 50.</p>
          </div>
          <select data-testid="admin-rank-position-filter" value={posFilter}
            onChange={(e) => { setPosFilter(e.target.value); loadPlayers(e.target.value); }}
            className="border border-slate-300 rounded-sm px-3 py-2 text-sm">
            {POSITION_FILTERS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div className="mt-4 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["Rank", "Player", "Pos", "Prov", "CBR Rating", "Stats", "Metrics", "Eval"].map((h) => <th key={h} className="text-left p-3 label-overline">{h}</th>)}
                <th className="text-right p-3 label-overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => <RankRow key={p.id} p={p} onEdit={setEditing} onToggleHidden={toggleHidden} />)}
              {players.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-sm text-slate-500">No players.</td></tr>}
            </tbody>
          </table>
        </div>
        <EditRankDialog player={editing} onClose={() => setEditing(null)} onSave={setOverride} />
      </div>
    </AdminLayout>
  );
}

function WeightSlider({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-[#0A192F]">{label}</span>
        <span className="font-data">{Math.round((value || 0) * 100)}%</span>
      </div>
      <input
        data-testid={testid}
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-[#E63946]"
      />
    </div>
  );
}

function RankRow({ p, onEdit, onToggleHidden }) {
  const rc = p.rating_components || {};
  const statTip = rc.stat_raw != null ? `Raw ${fmt(rc.stat_raw)} × ${Math.round((rc.stat_reliability || 0) * 100)}% sample confidence` : "No game stats (counts as 50)";
  return (
    <tr data-testid={`admin-rank-row-${p.id}`} className={`border-b border-slate-100 last:border-0 ${p.rank == null ? "opacity-50" : ""}`}>
      <td className="p-3 font-data text-lg" data-testid={`admin-rank-value-${p.id}`}>
        {p.rank != null ? `#${p.rank}` : "—"}
        {p.national_rank != null && p.national_rank !== p.rank && <span className="block text-[10px] text-slate-400">#{p.national_rank} national</span>}
        {p.rank_override != null && <Pin className="inline w-3 h-3 ml-1 text-[#E63946]" data-testid={`admin-rank-pinned-${p.id}`} />}
      </td>
      <td className="p-3 font-semibold">{p.first_name} {p.last_name}</td>
      <td className="p-3 font-data">{p.primary_position || "—"}</td>
      <td className="p-3">{p.province || "—"}</td>
      <td className="p-3 font-data text-lg" data-testid={`admin-rank-rating-${p.id}`}>{fmt(p.cbr_overall_rating)}</td>
      <td className="p-3 font-data" title={statTip}>{fmt(rc.stat_score)}</td>
      <td className="p-3 font-data">{fmt(rc.metric_score)}</td>
      <td className="p-3 font-data">{fmt(rc.evaluation_score)}</td>
      <td className="p-3 text-right whitespace-nowrap">
        {p.rank != null && (
          <button data-testid={`edit-rank-btn-${p.id}`} onClick={() => onEdit(p)}
            className="inline-flex items-center gap-1 px-3 py-1.5 mr-2 text-[10px] font-semibold uppercase tracking-wider rounded-sm border border-slate-300 hover:bg-slate-50">
            <Pencil className="w-3 h-3" /> Edit Rank
          </button>
        )}
        <button data-testid={`toggle-hidden-${p.id}`} onClick={() => onToggleHidden(p)}
          className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${p.hidden_from_rankings ? "bg-slate-300 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>
          {p.hidden_from_rankings ? <><EyeOff className="w-3 h-3" /> Hidden</> : <><Eye className="w-3 h-3" /> Visible</>}
        </button>
      </td>
    </tr>
  );
}
